import React, { FC, useContext, useMemo } from 'react';
import styles from './index.module.scss';
import CardBox from '../../CardBox';
import { Progress, Spin } from 'antd';
import { Empty } from '@maxtropy/components';
import { useRequest } from 'ahooks';
import { apiV2PaintingDashboardEnergyOverviewPost } from '@maxtropy/device-customer-apis-v2';
import { keepOneAndWanOrLine, keepOneOrLine } from '@/utils/utils';
import iconElecMonth from '../../../imgs/icon_elec_month.png';
import iconAirMonth from '../../../imgs/icon_air_month.png';
import iconNaturalGasMonth from '../../../imgs/icon_natural_gas_month.png';
import iconElecYear from '../../../imgs/icon_elec_year.png';
import iconAirYear from '../../../imgs/icon_air_year.png';
import iconNaturalGasYear from '../../../imgs/icon_natural_gas_year.png';
import bannerSection from '../../../imgs/banner_section.png';
import classNames from 'classnames';
import { ShareDataWrapperContext } from '../../../index';

interface EnergyCardItem {
  icon: string;
  label: string;
  unit: string;
  value: string;
  ratio: string;
  ratioNum: number;
  progressColor: string;
}

const EnergyCard: FC<EnergyCardItem> = ({ icon, label, unit, value, ratio, ratioNum, progressColor }) => (
  <div className={styles.energy_card}>
    <div className={styles.energy_card_top}>
      <img className={styles.energy_card_icon} src={icon} alt={label} />
      <div className={styles.energy_card_info}>
        <span className={styles.energy_card_label}>
          {label}({unit})
        </span>
        <span className={styles.energy_card_value}>{value}</span>
      </div>
    </div>
    <Progress
      percent={ratioNum}
      showInfo={false}
      strokeColor={progressColor}
      trailColor="rgba(255, 255, 255, 0.1)"
      strokeWidth={6}
    />
    <div className={styles.energy_card_ratio}>
      <span className={styles.energy_card_ratio_label}>全厂占比</span>
      <span className={styles.energy_card_ratio_value}>{ratio}%</span>
    </div>
  </div>
);

const TotalEnergy: FC = () => {
  const { data, loading } = useRequest(() => apiV2PaintingDashboardEnergyOverviewPost({}));
  const { isStandardSize } = useContext(ShareDataWrapperContext);

  const monthCards: EnergyCardItem[] = useMemo(() => {
    if (!data?.monthlyData) return [];
    return [
      {
        icon: iconElecMonth,
        label: '用电量',
        unit: 'kWh',
        value: keepOneAndWanOrLine(data.monthlyData.electricityTotal),
        ratio: keepOneOrLine(data.monthlyData.electricityRatio),
        ratioNum: data.monthlyData.electricityRatio ?? 0,
        progressColor: '#16DD8E',
      },
      {
        icon: iconAirMonth,
        label: '压缩空气',
        unit: 'm³',
        value: keepOneAndWanOrLine(data.monthlyData.airTotal),
        ratio: keepOneOrLine(data.monthlyData.airRatio),
        ratioNum: data.monthlyData.airRatio ?? 0,
        progressColor: '#16DD8E',
      },
      {
        icon: iconNaturalGasMonth,
        label: '天然气',
        unit: 'm³',
        value: keepOneAndWanOrLine(data.monthlyData.gasTotal),
        ratio: keepOneOrLine(data.monthlyData.gasRatio),
        ratioNum: data.monthlyData.gasRatio ?? 0,
        progressColor: '#16DD8E',
      },
    ];
  }, [data]);

  const yearCards: EnergyCardItem[] = useMemo(() => {
    if (!data?.yearlyData) return [];
    return [
      {
        icon: iconElecYear,
        label: '用电量',
        unit: 'kWh',
        value: keepOneAndWanOrLine(data.yearlyData.electricityTotal),
        ratio: keepOneOrLine(data.yearlyData.electricityRatio),
        ratioNum: data.yearlyData.electricityRatio ?? 0,
        progressColor: '#2D8DFF',
      },
      {
        icon: iconAirYear,
        label: '压缩空气',
        unit: 'm³',
        value: keepOneAndWanOrLine(data.yearlyData.airTotal),
        ratio: keepOneOrLine(data.yearlyData.airRatio),
        ratioNum: data.yearlyData.airRatio ?? 0,
        progressColor: '#2D8DFF',
      },
      {
        icon: iconNaturalGasYear,
        label: '天然气',
        unit: 'm³',
        value: keepOneAndWanOrLine(data.yearlyData.gasTotal),
        ratio: keepOneOrLine(data.yearlyData.gasRatio),
        ratioNum: data.yearlyData.gasRatio ?? 0,
        progressColor: '#2D8DFF',
      },
    ];
  }, [data]);

  // 大屏布局：直接渲染内容，不使用 CardBox
  if (!isStandardSize) {
    if (loading) {
      return (
        <div className={styles.loading_wrapper}>
          <Spin />
        </div>
      );
    }

    if (!data?.monthlyData && !data?.yearlyData) {
      return <Empty />;
    }

    return (
      <div className={classNames(styles.energy_container, styles.energy_container_horizontal)}>
        <div className={styles.energy_section}>
          <div className={styles.energy_section_box}>
            <div className={styles.energy_section_header} style={{ backgroundImage: `url(${bannerSection})` }}>
              <span className={styles.energy_section_title}>本月数据</span>
            </div>
            <div className={styles.energy_cards}>
              {monthCards.map(item => (
                <EnergyCard key={item.label} {...item} />
              ))}
            </div>
          </div>
        </div>
        <div className={styles.energy_section}>
          <div className={styles.energy_section_box}>
            <div className={styles.energy_section_header} style={{ backgroundImage: `url(${bannerSection})` }}>
              <span className={styles.energy_section_title}>本年数据</span>
            </div>
            <div className={styles.energy_cards}>
              {yearCards.map(item => (
                <EnergyCard key={item.label} {...item} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 标准布局：使用 CardBox
  if (loading) {
    return (
      <CardBox title="总能耗数据">
        <div className={styles.loading_wrapper}>
          <Spin />
        </div>
      </CardBox>
    );
  }

  if (!data?.monthlyData && !data?.yearlyData) {
    return (
      <CardBox title="总能耗数据">
        <Empty />
      </CardBox>
    );
  }

  return (
    <CardBox title="总能耗数据">
      <div className={styles.energy_container}>
        <div className={styles.energy_section} style={{ marginTop: 12 }}>
          <div className={styles.energy_section_box}>
            <div className={styles.energy_section_header} style={{ backgroundImage: `url(${bannerSection})` }}>
              <span className={styles.energy_section_title}>本月数据</span>
            </div>
            <div className={styles.energy_cards}>
              {monthCards.map(item => (
                <EnergyCard key={item.label} {...item} />
              ))}
            </div>
          </div>
        </div>
        <div className={styles.energy_section}>
          <div className={styles.energy_section_box}>
            <div className={styles.energy_section_header} style={{ backgroundImage: `url(${bannerSection})` }}>
              <span className={styles.energy_section_title}>本年数据</span>
            </div>
            <div className={styles.energy_cards}>
              {yearCards.map(item => (
                <EnergyCard key={item.label} {...item} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </CardBox>
  );
};

export default TotalEnergy;
